import {
  DatiSessioneUtente,
  DatiTitolare,
  EnumRichiestaDTOStato,
  EnumRichiestaDTOTipoIntegrazione,
  EnumTipoDomandaDTOStato,
  RichiestaDTO,
  RichiestaDTOStato,
  RichiestaModuloDTO,
  TipoDomandaDTO,
} from '@framework/api-client';
import { isInquiryInTesting, isWidgetIntegrable } from '@framework/common-web';
import { Widget } from '@framework/engine';
import { t } from '@framework/i18n';
import { isEmpty } from 'lodash';
import { hasAccessToAllModules } from '../module/hasAccessToAllModules';

export class SubmissionLogic {
  static canIntegrate(
    inquiry: TipoDomandaDTO | null | undefined,
    submission: RichiestaDTO | null | undefined,
  ): boolean {
    // E possibile integrare (duplicare) la richiesta solo se questa è in stato INVIATA, RICEVUTA
    if (
      submission?.stato != EnumRichiestaDTOStato.INVIATA &&
      submission?.stato != EnumRichiestaDTOStato.RICEVUTA
    )
      return false;

    // e ha il tipo_integrazione_permessa uguale a INTEGRAZIONE_VOLONTARIA o RICHIESTA_INTEGRAZIONE.
    if (submission.tipoIntegrazione == EnumRichiestaDTOTipoIntegrazione.NESSUNA)
      return false;

    // Inoltre solo l’integrazione volontaria non è consentita se il tipo di domanda
    // ha il flag blocca integrazione volontaria uguale a true
    if (
      submission.tipoIntegrazione ==
        EnumRichiestaDTOTipoIntegrazione.INTEGRAZIONE_VOLONTARIA &&
      inquiry?.bloccaIntegrazioneVolontaria == true
    )
      return false;

    // Se la domanda è già stata integrata, non è possibile integrare nuovamente
    if (submission.flHasIntegrazioni === true) return false;

    return true;
  }

  /**
   * Controlla se una richiesta è in stato di integrazione
   */
  static hasIntegrations(submission: RichiestaDTO | null | undefined): boolean {
    return (
      // 1. La richiesta ha una richiesta originale, quindi è una integrazione
      submission?.richiestaOriginale != null ||
      // 2. La richiesta è in stato INTEGRATA (es. prima richiesta, dopo essere stata integrata, è in stato INTEGRATA)
      submission?.stato == EnumRichiestaDTOStato.INTEGRATA
    );
  }

  /**
   * Controlla se una richiesta ha un numero di procedimento / protocollo.
   * In quel caso nascondiamo il codice della richiesta.
   */
  static getCodes(submission: RichiestaDTO | null | undefined) {
    const hasNumbers =
      !!submission?.numeroProcedimento || !!submission?.numeroProtocollo;
    if (hasNumbers) {
      return {
        protocolNumber: submission?.numeroProtocollo,
        procedureNumber: submission?.numeroProcedimento,
      };
    }

    return {
      submissionCode: submission?.id,
    };
  }

  static cannotSendAsDelegate(
    submission: RichiestaDTO | null | undefined,
    user: DatiSessioneUtente | null | undefined,
  ) {
    return (
      submission?.datiDelega?.permetteInvio == false &&
      user?.utenteLoggato?.idUtente == submission?.idDelegato
    );
  }

  /**
   * Disabilito il caricamento del file firmato
   */
  static cannotSignAsDelegate(
    submission: RichiestaDTO | null | undefined,
    user: DatiSessioneUtente | null | undefined,
  ) {
    return (
      submission?.datiDelega?.permetteFirma === false &&
      user?.utenteLoggato?.idUtente == submission?.idDelegato
    );
  }

  static getApplicant(submission: RichiestaDTO | null | undefined) {
    if (!submission) return null;
    if (!submission.datiTitolari?.length) return null;

    const mainOwnerData = submission.datiTitolari?.find(
      a => a.flTitolarePrincipale,
    );

    const mainOwner = mainOwnerData
      ? this.getApplicantLabel(mainOwnerData)
      : null;

    // inseriamo il codice fiscale del titolare principale
    const mainOwnerFiscalCode = mainOwnerData?.codiceFiscale ?? '';

    const others = submission.datiTitolari
      .filter(t => !t.flTitolarePrincipale)
      .map(t => this.getApplicantLabel(t));

    const multipleApplicants = others.length > 0;

    return { mainOwner, others, multipleApplicants, mainOwnerFiscalCode };
  }

  /**
   * Ottiene l'etichetta del titolare
   */
  static getApplicantLabel(applicant: DatiTitolare) {
    const legalEntityLabel = this.getLegalEntityLabel(applicant);
    if (legalEntityLabel) return legalEntityLabel;

    const fiscalNumber = isEmpty(applicant.codiceFiscale?.trim())
      ? t('submission-list.unknown-applicant', 'non identificato')
      : applicant.codiceFiscale!;

    return isEmpty(applicant.nome?.trim()) || isEmpty(applicant.cognome?.trim())
      ? fiscalNumber
      : `${applicant.nome ?? ''} ${applicant.cognome ?? ''}`;
  }

  /**
   * Etichetta della Persona Giuridica del titolare, se valorizzata:
   * denominazione (`nome`) con fallback sulla partita IVA.
   * Restituisce null quando il titolare non è un soggetto giuridico valorizzato (Persona Fisica)
   */
  static getLegalEntityLabel(applicant: DatiTitolare): string | null {
    const legalEntity = applicant.soggettoGiuridico;
    if (!legalEntity) return null;

    return legalEntity.nome?.trim() || legalEntity.partitaIva?.trim() || null;
  }

  /**
   * Controlla se è possibile creare una nuova richiesta
   */
  static canCreateSubmission(inquiry: TipoDomandaDTO | undefined) {
    // Una domanda in collaudo può essere sempre compilata
    if (SubmissionLogic.isInTesting(inquiry)) {
      return true;
    }

    // Controllo su utenti abilitati
    if (
      inquiry?.flAttivaUtentiAbilitati &&
      inquiry?.flUtenteCorrenteAbilitato === false
    ) {
      return false;
    }

    return inquiry?.stato === EnumTipoDomandaDTOStato.APERTA;
  }

  static isInTesting(inquiry: TipoDomandaDTO | undefined) {
    return isInquiryInTesting(inquiry);
  }

  static canShowCommunications(
    submissionState: RichiestaDTOStato | null | undefined,
  ) {
    return submissionState !== EnumRichiestaDTOStato.INVIATA;
  }

  /**
   * Controlla se è possibile duplicare una richiesta controllando il flag di univocità
   */
  static canDuplicate(submission: RichiestaDTO, inquiries: TipoDomandaDTO[]) {
    return !inquiries.find(
      inquiry =>
        inquiry.idGruppo === submission.idGruppo && inquiry.flRichiestaUnivoca,
    );
  }

  /**
   * Ritorna vero se la richiesta è una integrazione a un'altra richiesta
   */
  static isIntegration(submission: RichiestaDTO | null | undefined) {
    if (!submission) return false;
    return submission.richiestaOriginale != null;
  }

  /**
   * Se siamo in un'integrazione, ritorna la funzione che controlla
   * quando un widget è integrabile o meno (utile per mettere il widget
   * in readonly).
   *
   * Se ci troviamo nella richiesta principale, ritorna null (i widget non devono
   * prendere in considerazione la direttiva di integrabilità.)
   */
  static getIsWidgetReadonlyFunction(
    submission: RichiestaDTO | null | undefined,
  ) {
    // Se non è un'integrazione ritorna undefined
    if (!this.isIntegration(submission)) return undefined;
    return (widget: Widget) => !isWidgetIntegrable(widget);
  }

  /**
   * Ritorna vero se la richiesta è in stato di pagamento
   */
  static isSubmissionInPayment(
    submission: Pick<RichiestaDTO, 'stato'> | null | undefined,
  ) {
    return submission?.stato === EnumRichiestaDTOStato.PAGAMENTO;
  }

  /**
   * Ritorna vero se la richiesta è stata già inviata.
   * Utilizzato per il redirect alla pagina "inviata"
   */
  static isSubmissionSent(
    submission: Pick<RichiestaDTO, 'stato'> | null | undefined,
  ) {
    return (
      submission?.stato === EnumRichiestaDTOStato.INVIATA ||
      submission?.stato === EnumRichiestaDTOStato.RICEVUTA ||
      submission?.stato === EnumRichiestaDTOStato.INTEGRATA ||
      submission?.stato === EnumRichiestaDTOStato.CONCLUSA
    );
  }

  /**
   * Ritorna vero se la richiesta è IN_INVIO, ovvero non è modificabile, ma
   * non è nemmeno inviata.
   */
  static isSubmissionPending(submission: RichiestaDTO | null | undefined) {
    return submission?.stato === EnumRichiestaDTOStato.IN_INVIO;
  }

  /**
   * Ritorna vero se la richiesta è stata appena creata
   */
  static isSubmissionNew(submission: RichiestaDTO | null | undefined) {
    return submission?.stato === EnumRichiestaDTOStato.CREATA;
  }

  /**
   * Se la richiesta è in pagamento o è inviata, il modulo è in sola lettura
   */
  static isSubmissionReadonly(submission: RichiestaDTO | null | undefined) {
    return (
      this.isSubmissionSent(submission) ||
      this.isSubmissionInPayment(submission)
    );
  }

  /**
   * Se la richiesta è stata conclusa, il modulo è in sola lettura e
   * inoltre non è possibile eseguire i pagamenti
   */
  static isSubmissionConcluded(submission: RichiestaDTO | null | undefined) {
    return submission?.stato === EnumRichiestaDTOStato.CONCLUSA;
  }

  /**
   * Controlliamo che la richiesta sia pagabile (NO CONCLUSA NE INTEGRATA)
   */
  static isSubmissionPayable(submission: RichiestaDTO | null | undefined) {
    return (
      submission?.stato !== EnumRichiestaDTOStato.CONCLUSA &&
      submission?.stato !== EnumRichiestaDTOStato.INTEGRATA
    );
  }

  /**
   * Ritorna vero se l'utente è il richiedente (autore) della richiesta
   */
  static isAuthor(
    currentUser: DatiSessioneUtente | null,
    submission: RichiestaDTO | null | undefined,
  ) {
    if (!currentUser || !submission) return false;
    return submission?.idRichiedente === currentUser?.utenteLoggato?.idUtente;
  }

  /**
   * Domande pubblicate in sola lettura
   */
  static isInquiryPreview(inquiry: TipoDomandaDTO | null | undefined) {
    return inquiry?.flBloccaCompilazioneInvio === true;
  }

  /**
   * Ritorna vero se per la richiesta deve essere mostrato il pulsante di
   * download del PDF consolidato (tutti i moduli in un unico documento).
   */
  static showConsolidatedPdf(
    submission: RichiestaDTO | null | undefined,
    inquiry: TipoDomandaDTO | null | undefined,
  ) {
    if (this.isInquiryPreview(inquiry)) return false;

    return this.isSubmissionSent(submission);
  }

  /**
   * Come {@link showConsolidatedPdf}, ma verifica anche che l'utente abbia
   * accesso a *tutti* i moduli della richiesta.
   */
  static canDownloadConsolidatedPdf(
    submission: RichiestaDTO | null | undefined,
    inquiry: TipoDomandaDTO | null | undefined,
    modules: RichiestaModuloDTO[],
  ) {
    return (
      this.showConsolidatedPdf(submission, inquiry) &&
      hasAccessToAllModules(modules)
    );
  }
}
